import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Save, Check, Clock, Award, Layers, FileQuestion, Crown } from "lucide-react";
import Layout from "../../components/Layout";
import PrizeDistributionEditor from "../../components/PrizeDistributionEditor";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";

const COLORS = [
  "from-cyan-500 to-cyan-600 border-cyan-300",
  "from-fuchsia-500 to-fuchsia-600 border-fuchsia-300",
  "from-pink-500 to-pink-600 border-pink-300",
  "from-emerald-500 to-emerald-600 border-emerald-300",
];

const newOption = () => ({ id: rid(), text: "" });
const newQuestion = () => {
  const opts = [newOption(), newOption(), newOption(), newOption()];
  return {
    id: rid(),
    text: "",
    options: opts,
    correct_option_id: opts[0].id,
  };
};
const newQuiz = (title = "Quiz") => ({
  id: rid(),
  title,
  default_question_time_seconds: 20,
  default_question_points: 1000,
  questions: [newQuestion()],
});
function rid() {
  return Math.random().toString(16).slice(2, 10);
}

function toLocalInput(date) {
  if (!date) return "";
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalInput(s) {
  if (!s) return null;
  return new Date(s).toISOString();
}

export default function ChallengeEditor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = Boolean(id);

  const [type, setType] = useState("single");
  const [category, setCategory] = useState("standard");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priceAmount, setPriceAmount] = useState(1.0);
  const [startsAt, setStartsAt] = useState(toLocalInput(Date.now() + 60 * 60 * 1000));
  const [endsAt, setEndsAt] = useState(toLocalInput(Date.now() + 25 * 60 * 60 * 1000));
  const [rewardDescription, setRewardDescription] = useState("");
  const [prizePoolBase, setPrizePoolBase] = useState(0);
  const [winnersCount, setWinnersCount] = useState(1);
  const [prizeDistribution, setPrizeDistribution] = useState([{ rank: 1, percentage: 100 }]);
  const [quizzes, setQuizzes] = useState([newQuiz("Quiz 1")]);
  const [activeQuizIdx, setActiveQuizIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(!editing);
  const [activity, setActivity] = useState(null);
  // v2.39 — reward + access model
  const [rewardType, setRewardType] = useState("money");
  const [accessMode, setAccessMode] = useState("public_with_code");
  const [entryMode, setEntryMode] = useState("paid");
  const [qualDestId, setQualDestId] = useState("");
  const [qualCount, setQualCount] = useState(3);
  const [qualMessage, setQualMessage] = useState("");
  // Finales list (destinations for qualification): challenges with
  // access_mode='qualified_only' that are still editable/publishable.
  const [finales, setFinales] = useState([]);

  useEffect(() => {
    if (!editing) return;
    (async () => {
      try {
        const [{ data }, actRes] = await Promise.all([
          api.get(`/challenges/${id}`),
          api.get(`/admin/challenges/${id}/activity`).catch(() => ({ data: null })),
        ]);
        if (actRes?.data) setActivity(actRes.data);
        setType(data.type);
        setCategory(data.category || "standard");
        setTitle(data.title);
        setDescription(data.description || "");
        setPriceAmount(data.price_amount);
        setStartsAt(toLocalInput(data.starts_at));
        setEndsAt(toLocalInput(data.ends_at));
        setRewardDescription(data.reward_description || "");
        setPrizePoolBase(data.prize_pool_base ?? 0);
        setWinnersCount(data.winners_count ?? 1);
        if (Array.isArray(data.prize_distribution) && data.prize_distribution.length) {
          setPrizeDistribution(
            data.prize_distribution.map((d, i) => ({
              rank: d.rank ?? i + 1,
              percentage: Number(d.percentage) || 0,
            })),
          );
        }
        setQuizzes(data.quizzes);
        // v2.39 — reward + access model (defaults keep legacy docs working)
        setRewardType(data.reward_type || "money");
        setAccessMode(data.access_mode || "public_with_code");
        setEntryMode(data.entry_mode || "paid");
        const qc = data.qualification_config || null;
        if (qc) {
          setQualDestId(qc.destination_challenge_id || "");
          setQualCount(qc.qualified_count ?? 3);
          setQualMessage(qc.message || "");
        }
      } catch (e) {
        toast.error(formatApiError(e));
        navigate("/admin");
      } finally {
        setLoaded(true);
      }
    })();
  }, [editing, id, navigate]);

  // v2.39 — fetch available finales (challenges with access_mode='qualified_only')
  // so admin can pick one when creating a qualifier quiz.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/challenges?archived=active");
        setFinales(
          (data || []).filter(
            (c) => c.access_mode === "qualified_only" && c.id !== id,
          ),
        );
      } catch {
        // silent — dropdown will show "aucune finale disponible"
      }
    })();
  }, [id]);

  // v2.39 — multi-quiz series has been removed. Force ``single`` + 1 quiz.
  useEffect(() => {
    if (type !== "single") setType("single");
    if (quizzes.length !== 1) setQuizzes([quizzes[0] || newQuiz("Quiz 1")]);
    setActiveQuizIdx(0);
  }, [type]);

  const activeQuiz = quizzes[activeQuizIdx];

  const updateActiveQuiz = (patch) => {
    setQuizzes((qs) =>
      qs.map((q, i) => (i === activeQuizIdx ? { ...q, ...patch } : q)),
    );
  };
  const updateQuestion = (qIdx, patch) => {
    updateActiveQuiz({
      questions: activeQuiz.questions.map((q, i) => (i === qIdx ? { ...q, ...patch } : q)),
    });
  };
  const updateOption = (qIdx, oIdx, text) => {
    updateActiveQuiz({
      questions: activeQuiz.questions.map((q, i) =>
        i === qIdx
          ? { ...q, options: q.options.map((o, j) => (j === oIdx ? { ...o, text } : o)) }
          : q,
      ),
    });
  };
  const addQuestion = () =>
    updateActiveQuiz({ questions: [...activeQuiz.questions, newQuestion()] });
  const removeQuestion = (qIdx) => {
    if (activeQuiz.questions.length <= 1) return;
    updateActiveQuiz({ questions: activeQuiz.questions.filter((_, i) => i !== qIdx) });
  };

  const addQuiz = () => {
    setQuizzes((qs) => [...qs, newQuiz(`Quiz ${qs.length + 1}`)]);
    setActiveQuizIdx(quizzes.length);
  };
  const removeQuiz = (idx) => {
    if (quizzes.length <= 1) return;
    const next = quizzes.filter((_, i) => i !== idx);
    setQuizzes(next);
    setActiveQuizIdx(Math.max(0, Math.min(activeQuizIdx, next.length - 1)));
  };

  const save = async () => {
    if (!title.trim()) return toast.error("Donne un titre au défi");
    if (!startsAt || !endsAt) return toast.error("Définis la fenêtre de participation");
    // v2.39 — validate qualification config
    if (rewardType === "qualification") {
      if (!qualDestId) {
        return toast.error("Choisis un quiz de destination pour la qualification.");
      }
      if (!Number.isFinite(qualCount) || qualCount < 1) {
        return toast.error("Le nombre de qualifiés doit être ≥ 1.");
      }
      if (accessMode === "qualified_only") {
        return toast.error(
          "Un quiz qualificatif ne peut pas être réservé aux qualifiés.",
        );
      }
    }
    for (let qi = 0; qi < quizzes.length; qi++) {
      const qz = quizzes[qi];
      if (!qz.title.trim()) return toast.error(`Quiz ${qi + 1} : titre manquant`);
      for (let i = 0; i < qz.questions.length; i++) {
        const q = qz.questions[i];
        if (!q.text.trim()) return toast.error(`Quiz ${qi + 1} / Q${i + 1} : énoncé manquant`);
        const filled = q.options.filter((o) => o.text.trim());
        if (filled.length < 2) return toast.error(`Quiz ${qi + 1} / Q${i + 1} : au moins 2 réponses`);
        if (!filled.find((o) => o.id === q.correct_option_id))
          return toast.error(`Quiz ${qi + 1} / Q${i + 1} : sélectionne la bonne réponse`);
      }
    }
    if (Number(prizePoolBase) < 0) return toast.error("La cagnotte de base ne peut pas être négative");
    if (Number(winnersCount) < 1 || Number(winnersCount) > 10) return toast.error("Nombre de gagnants entre 1 et 10");
    // Validate distribution: total 100 ± 0.01, no zero/negative, exact length.
    if (prizeDistribution.length !== Number(winnersCount)) {
      return toast.error("La répartition doit contenir exactement " + winnersCount + " place(s).");
    }
    if (prizeDistribution.some((d) => !Number(d.percentage) || Number(d.percentage) <= 0)) {
      return toast.error("Chaque pourcentage doit être strictement supérieur à 0.");
    }
    const totalPct = Math.round(
      prizeDistribution.reduce((a, d) => a + Number(d.percentage), 0) * 100,
    ) / 100;
    if (Math.abs(totalPct - 100) > 0.01) {
      return toast.error("Le total de la répartition doit être égal à 100 % (actuel : " + totalPct + " %).");
    }
    const payload = {
      title: title.trim(),
      description: description.trim(),
      type,
      category,
      price_amount: entryMode === "free" ? 0 : Number(priceAmount) || 0,
      price_currency: "eur",
      starts_at: fromLocalInput(startsAt),
      ends_at: fromLocalInput(endsAt),
      reward_description: rewardDescription.trim(),
      prize_pool_base:
        rewardType === "money"
          ? Math.max(0, Number(prizePoolBase) || 0)
          : 0,
      winners_count: Number(winnersCount) || 1,
      prize_distribution: prizeDistribution.map((d, i) => ({
        rank: d.rank ?? i + 1,
        percentage: Number(d.percentage) || 0,
      })),
      status: "draft",
      // v2.39 — reward + access model
      reward_type: rewardType,
      qualification_config:
        rewardType === "qualification"
          ? {
              destination_challenge_id: qualDestId,
              qualified_count: Number(qualCount) || 3,
              message: qualMessage.trim(),
            }
          : null,
      access_mode: accessMode,
      entry_mode: entryMode,
      quizzes: quizzes.map((qz) => ({
        id: qz.id,
        title: qz.title.trim(),
        default_question_time_seconds: Number(qz.default_question_time_seconds) || 20,
        default_question_points: Number(qz.default_question_points) || 1000,
        questions: qz.questions.map((q) => ({
          id: q.id,
          text: q.text.trim(),
          options: q.options.filter((o) => o.text.trim()).map((o) => ({ id: o.id, text: o.text.trim() })),
          correct_option_id: q.correct_option_id,
        })),
      })),
    };
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/challenges/${id}`, payload);
        toast.success("Défi mis à jour");
      } else {
        await api.post("/challenges", payload);
        toast.success("Défi créé (en brouillon)");
      }
      navigate("/admin");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-6 py-16 text-white/50 font-mono-stat uppercase">Chargement...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-6 py-10 pb-32">
        <button
          data-testid="editor-back-button"
          onClick={() => navigate("/admin")}
          className="flex items-center gap-2 text-white/60 hover:text-cyan-300 mb-6 text-sm font-mono-stat uppercase tracking-widest"
        >
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>

        <h1 className="font-display font-black uppercase tracking-tight text-4xl mb-8">
          {editing ? "Éditer le défi" : "Nouveau défi"}
        </h1>

        {editing && activity && (
          activity.has_activity ? (
            <div
              data-testid="editor-activity-blocked"
              className="rounded-2xl border border-rose-500/40 bg-rose-500/10 text-rose-200 px-5 py-4 mb-6 text-sm"
            >
              <div className="font-display font-bold uppercase tracking-widest text-xs mb-1">
                Édition bloquée
              </div>
              Ce défi ne peut plus être modifié librement —{" "}
              <span className="font-mono-stat">
                {activity.play_sessions} session(s) · {activity.reserved_codes} code(s) réservé(s) ·{" "}
                {activity.consumed_codes} consommé(s) · {activity.prize_claims} réclamation(s)
              </span>
              . Les changements seront refusés à l&apos;enregistrement.
            </div>
          ) : (
            <div
              data-testid="editor-activity-clean"
              className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-200 px-5 py-4 mb-6 text-sm"
            >
              <div className="font-display font-bold uppercase tracking-widest text-xs mb-1">
                Aucune participation — modification autorisée
              </div>
              Tu peux modifier ce défi entièrement (titre, dates, questions, cagnotte, répartition)
              et le republier comme un défi à venir.
            </div>
          )
        )}

        {/* Meta */}
        <div className="panel rounded-2xl p-6 mb-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <button
              type="button"
              data-testid="editor-category-standard"
              onClick={() => setCategory("standard")}
              className={`p-4 rounded-xl border text-left transition-all ${
                category === "standard"
                  ? "border-cyan-400 bg-cyan-400/10"
                  : "border-white/10 bg-black/40 hover:border-white/30"
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <FileQuestion className="w-5 h-5 text-cyan-300" />
                <span className="font-display font-bold">Catégorie standard</span>
              </div>
              <p className="text-xs text-white/55">
                Défi accessible avec un code standard (1 €, packs 5 et 50).
              </p>
            </button>
            <button
              type="button"
              data-testid="editor-category-golden"
              onClick={() => setCategory("golden")}
              className={`p-4 rounded-xl border text-left transition-all ${
                category === "golden"
                  ? "border-amber-400 bg-amber-400/10 shadow-[0_0_25px_rgba(251,191,36,0.25)]"
                  : "border-white/10 bg-black/40 hover:border-white/30"
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Crown className="w-5 h-5 text-amber-300" />
                <span className="font-display font-bold text-amber-200">Golden Quizz</span>
              </div>
              <p className="text-xs text-white/55">
                Défi exclusif — accessible uniquement avec un Golden Code.
              </p>
            </button>
          </div>

          <Input label="Titre">
            <input
              data-testid="editor-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={160}
              className="w-full bg-transparent outline-none font-display text-xl"
              placeholder="Le grand défi du week-end"
            />
          </Input>
          <Input label="Description">
            <textarea
              data-testid="editor-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={800}
              rows={2}
              className="w-full bg-transparent outline-none"
              placeholder="De quoi parle ce défi ?"
            />
          </Input>
          <div className="grid sm:grid-cols-2 gap-3">
            <Input label="Début (date + heure)">
              <input
                data-testid="editor-starts-input"
                type="datetime-local"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                className="w-full bg-transparent outline-none"
              />
            </Input>
            <Input label="Fin (date + heure)">
              <input
                data-testid="editor-ends-input"
                type="datetime-local"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
                className="w-full bg-transparent outline-none"
              />
            </Input>
          </div>

          {/* v2.39 — Reward / Access / Entry model */}
          <div className="rounded-xl border border-fuchsia-400/30 bg-fuchsia-400/5 p-4 space-y-4">
            <div className="text-xs uppercase tracking-[0.3em] text-fuchsia-300 font-mono-stat">
              Récompense &amp; accès
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <button
                type="button"
                data-testid="editor-reward-type-money"
                onClick={() => setRewardType("money")}
                className={`p-3 rounded-xl border text-left transition-all ${
                  rewardType === "money"
                    ? "border-emerald-400 bg-emerald-400/10"
                    : "border-white/10 bg-black/40 hover:border-white/30"
                }`}
              >
                <div className="font-display font-bold text-sm">💶 Récompense en argent</div>
                <p className="text-[11px] text-white/55 mt-1">
                  Cagnotte partagée entre les gagnants.
                </p>
              </button>
              <button
                type="button"
                data-testid="editor-reward-type-qualification"
                onClick={() => setRewardType("qualification")}
                className={`p-3 rounded-xl border text-left transition-all ${
                  rewardType === "qualification"
                    ? "border-cyan-400 bg-cyan-400/10"
                    : "border-white/10 bg-black/40 hover:border-white/30"
                }`}
              >
                <div className="font-display font-bold text-sm">🎫 Qualification</div>
                <p className="text-[11px] text-white/55 mt-1">
                  Les meilleurs sont qualifiés pour une finale.
                </p>
              </button>
              <button
                type="button"
                data-testid="editor-reward-type-none"
                onClick={() => setRewardType("none")}
                className={`p-3 rounded-xl border text-left transition-all ${
                  rewardType === "none"
                    ? "border-white/50 bg-white/10"
                    : "border-white/10 bg-black/40 hover:border-white/30"
                }`}
              >
                <div className="font-display font-bold text-sm">🎯 Sans récompense</div>
                <p className="text-[11px] text-white/55 mt-1">
                  Pour le fun, classement seulement.
                </p>
              </button>
            </div>

            {rewardType === "qualification" && (
              <div className="space-y-3 pt-2 border-t border-white/10">
                <Input label="Quiz de destination (finale) *">
                  <select
                    data-testid="editor-qual-dest-select"
                    value={qualDestId}
                    onChange={(e) => setQualDestId(e.target.value)}
                    className="w-full bg-transparent outline-none text-white"
                  >
                    <option value="" className="bg-black">
                      — Choisir une finale —
                    </option>
                    {finales.length === 0 && (
                      <option value="" disabled className="bg-black text-white/40">
                        Aucune finale en accès qualified_only — crées-en une d&apos;abord
                      </option>
                    )}
                    {finales.map((f) => (
                      <option key={f.id} value={f.id} className="bg-black">
                        {f.title} — {f.prize_pool_base ?? 0} €
                      </option>
                    ))}
                  </select>
                </Input>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input label="Nombre de qualifiés (top-N)">
                    <input
                      data-testid="editor-qual-count-input"
                      type="number"
                      min={1}
                      max={100}
                      value={qualCount}
                      onChange={(e) => setQualCount(Number(e.target.value) || 1)}
                      className="w-full bg-transparent outline-none font-mono-stat font-bold text-xl"
                    />
                  </Input>
                  <Input label="Message aux qualifiés (optionnel)">
                    <input
                      data-testid="editor-qual-message-input"
                      value={qualMessage}
                      onChange={(e) => setQualMessage(e.target.value)}
                      maxLength={300}
                      className="w-full bg-transparent outline-none"
                      placeholder="Ex : Rendez-vous vendredi 20 h !"
                    />
                  </Input>
                </div>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-white/10">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/50 font-mono-stat mb-1">
                  Mode d&apos;accès
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="editor-access-mode-public"
                    onClick={() => setAccessMode("public_with_code")}
                    className={`p-2 rounded-lg border text-xs text-left transition-all ${
                      accessMode === "public_with_code"
                        ? "border-cyan-400 bg-cyan-400/10"
                        : "border-white/10 bg-black/40 hover:border-white/30"
                    }`}
                  >
                    <div className="font-display font-bold">Public avec code</div>
                    <div className="text-white/55 text-[10px]">Accès via code standard/golden</div>
                  </button>
                  <button
                    type="button"
                    data-testid="editor-access-mode-qualified"
                    onClick={() => setAccessMode("qualified_only")}
                    className={`p-2 rounded-lg border text-xs text-left transition-all ${
                      accessMode === "qualified_only"
                        ? "border-amber-400 bg-amber-400/10"
                        : "border-white/10 bg-black/40 hover:border-white/30"
                    }`}
                  >
                    <div className="font-display font-bold">🏆 Finale (qualifiés uniquement)</div>
                    <div className="text-white/55 text-[10px]">Réservé aux joueurs qualifiés</div>
                  </button>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/50 font-mono-stat mb-1">
                  Entrée
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="editor-entry-mode-paid"
                    onClick={() => setEntryMode("paid")}
                    className={`p-2 rounded-lg border text-xs text-left transition-all ${
                      entryMode === "paid"
                        ? "border-cyan-400 bg-cyan-400/10"
                        : "border-white/10 bg-black/40 hover:border-white/30"
                    }`}
                  >
                    <div className="font-display font-bold">Payante</div>
                    <div className="text-white/55 text-[10px]">Achat de code requis</div>
                  </button>
                  <button
                    type="button"
                    data-testid="editor-entry-mode-free"
                    onClick={() => setEntryMode("free")}
                    className={`p-2 rounded-lg border text-xs text-left transition-all ${
                      entryMode === "free"
                        ? "border-emerald-400 bg-emerald-400/10"
                        : "border-white/10 bg-black/40 hover:border-white/30"
                    }`}
                  >
                    <div className="font-display font-bold">Gratuite</div>
                    <div className="text-white/55 text-[10px]">Comp code auto pour les inscrits</div>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <Input label="Cagnotte de base (€) — optionnelle">
              <input
                data-testid="editor-prize-pool-base-input"
                type="number"
                min={0}
                step={1}
                placeholder="0"
                value={prizePoolBase}
                onChange={(e) => setPrizePoolBase(Number(e.target.value))}
                className="w-full bg-transparent outline-none font-mono-stat font-bold text-xl"
              />
              <div className="text-[10px] text-white/40 mt-1.5 font-mono-stat">
                Montant ajouté par l&apos;organisateur. Laisser à 0 pour ne pas ajouter de base.
              </div>
            </Input>
            <Input label="Prix entrée (€)">
              <input
                data-testid="editor-price-input"
                type="number"
                min={0}
                step={0.5}
                value={priceAmount}
                onChange={(e) => setPriceAmount(Number(e.target.value))}
                className="w-full bg-transparent outline-none font-mono-stat font-bold text-xl"
              />
            </Input>
            <div className="rounded-xl bg-cyan-400/10 border border-cyan-400/30 p-3 flex flex-col justify-center">
              <div className="text-[10px] uppercase tracking-[0.25em] text-cyan-300 font-mono-stat mb-1">
                Cagnotte fixe
              </div>
              <div className="text-sm text-white/70">
                La cagnotte affichée ne dépend PAS du nombre d&apos;inscriptions —
                elle est strictement celle saisie à gauche.
              </div>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Input label="Nombre de gagnants (1 à 10)">
              <input
                data-testid="editor-winners-count-input"
                type="number"
                min={1}
                max={10}
                value={winnersCount}
                onChange={(e) => setWinnersCount(Number(e.target.value))}
                className="w-full bg-transparent outline-none font-mono-stat font-bold text-xl"
              />
            </Input>
            <div className="rounded-xl bg-cyan-400/10 border border-cyan-400/30 p-3 flex flex-col justify-center">
              <div className="text-[10px] uppercase tracking-[0.25em] text-cyan-300 font-mono-stat mb-1">
                Distribution
              </div>
              <div className="text-sm text-white/70">
                Les <span className="font-mono-stat font-bold text-cyan-200">{winnersCount}</span> premier{winnersCount > 1 ? "s" : ""} du classement pourront fournir leur IBAN.
              </div>
            </div>
          </div>

          <PrizeDistributionEditor
            winnersCount={Number(winnersCount) || 1}
            prizePoolBase={Number(prizePoolBase) || 0}
            value={prizeDistribution}
            onChange={setPrizeDistribution}
          />

          <Input label="Détail de la récompense (optionnel — texte d'accompagnement)">
            <input
              data-testid="editor-reward-input"
              value={rewardDescription}
              onChange={(e) => setRewardDescription(e.target.value)}
              maxLength={500}
              className="w-full bg-transparent outline-none"
              placeholder="Ex : Cash + lot mystère, virement sous 7 jours..."
            />
          </Input>
        </div>

        {/* Quiz editor — v2.39: single quiz per challenge */}
        <div className="panel rounded-2xl p-5 mb-6 space-y-4">
          <Input label="Titre du quiz">
            <input
              data-testid={`editor-quiz-title-${activeQuizIdx}`}
              value={activeQuiz.title}
              onChange={(e) => updateActiveQuiz({ title: e.target.value })}
              maxLength={120}
              className="w-full bg-transparent outline-none font-display text-lg"
            />
          </Input>
          {/* Quiz-level defaults: apply automatically to every question */}
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-black/40 border border-white/10">
              <Clock className="w-4 h-4 text-cyan-300" />
              <div className="flex-1">
                <div className="text-xs uppercase tracking-widest text-white/50 font-mono-stat">Temps / question</div>
                <div className="text-[9px] text-white/40 font-mono-stat">appliqué à toutes les questions</div>
              </div>
              <input
                data-testid={`editor-quiz-default-time-${activeQuizIdx}`}
                type="number"
                min={5}
                max={180}
                value={activeQuiz.default_question_time_seconds ?? 20}
                onChange={(e) => updateActiveQuiz({ default_question_time_seconds: Number(e.target.value) || 20 })}
                className="w-16 bg-transparent outline-none text-right font-mono-stat font-bold"
              />
              <span className="text-xs text-white/40">s</span>
            </label>
            <label className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-black/40 border border-white/10">
              <Award className="w-4 h-4 text-fuchsia-300" />
              <div className="flex-1">
                <div className="text-xs uppercase tracking-widest text-white/50 font-mono-stat">Points / question</div>
                <div className="text-[9px] text-white/40 font-mono-stat">appliqué à toutes les questions</div>
              </div>
              <input
                data-testid={`editor-quiz-default-points-${activeQuizIdx}`}
                type="number"
                min={100}
                max={5000}
                step={100}
                value={activeQuiz.default_question_points ?? 1000}
                onChange={(e) => updateActiveQuiz({ default_question_points: Number(e.target.value) || 1000 })}
                className="w-20 bg-transparent outline-none text-right font-mono-stat font-bold"
              />
            </label>
          </div>
        </div>

        <div className="space-y-6">
          {activeQuiz.questions.map((q, qIdx) => (
            <div
              key={q.id}
              data-testid={`editor-question-${qIdx}`}
              className="panel rounded-2xl p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="px-3 py-1 rounded-full bg-cyan-400/10 border border-cyan-400/30 text-cyan-300 text-xs uppercase tracking-widest font-mono-stat">
                  Question {qIdx + 1}
                </span>
                <button
                  data-testid={`editor-remove-question-${qIdx}`}
                  onClick={() => removeQuestion(qIdx)}
                  disabled={activeQuiz.questions.length <= 1}
                  className="p-2 rounded-lg bg-white/5 hover:bg-pink-500/20 hover:text-pink-300 disabled:opacity-30 transition-all"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <textarea
                data-testid={`editor-question-text-${qIdx}`}
                value={q.text}
                onChange={(e) => updateQuestion(qIdx, { text: e.target.value })}
                maxLength={400}
                rows={2}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-cyan-400/60 focus:glow-primary transition-all font-display text-lg mb-4"
                placeholder="Saisis ta question..."
              />
              <div className="grid sm:grid-cols-2 gap-3 mb-4">
                {q.options.map((opt, oIdx) => {
                  const isCorrect = q.correct_option_id === opt.id;
                  return (
                    <div key={opt.id} className="relative">
                      <button
                        data-testid={`editor-correct-${qIdx}-${oIdx}`}
                        type="button"
                        onClick={() => updateQuestion(qIdx, { correct_option_id: opt.id })}
                        className={`absolute -left-1 -top-1 w-7 h-7 rounded-lg flex items-center justify-center transition-all z-10 ${
                          isCorrect
                            ? "bg-emerald-400 text-black"
                            : "bg-black/60 border border-white/10 text-white/30 hover:text-white"
                        }`}
                        title={isCorrect ? "Bonne réponse" : "Marquer comme bonne réponse"}
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <input
                        data-testid={`editor-option-${qIdx}-${oIdx}`}
                        value={opt.text}
                        onChange={(e) => updateOption(qIdx, oIdx, e.target.value)}
                        maxLength={200}
                        placeholder={`Réponse ${oIdx + 1}`}
                        className={`w-full pl-10 pr-4 py-3 rounded-xl bg-gradient-to-br ${COLORS[oIdx]} bg-opacity-20 text-white border outline-none focus:ring-2 focus:ring-white/30 font-display font-semibold`}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="text-xs font-mono-stat text-white/40 mt-2">
                Temps {activeQuiz.default_question_time_seconds ?? 20} s · {activeQuiz.default_question_points ?? 1000} points — définis au niveau du quiz.
              </div>
            </div>
          ))}
        </div>

        <button
          data-testid="editor-add-question-button"
          onClick={addQuestion}
          className="mt-6 w-full flex items-center justify-center gap-2 py-4 rounded-2xl border-2 border-dashed border-white/15 hover:border-cyan-400/60 hover:text-cyan-300 transition-all font-display uppercase tracking-widest"
        >
          <Plus className="w-5 h-5" /> Ajouter une question
        </button>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-30 panel border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <span className="text-sm text-white/50 font-mono-stat uppercase tracking-widest">
            {type === "series" ? `${quizzes.length} quiz · ` : ""}
            {quizzes.reduce((acc, q) => acc + q.questions.length, 0)} question
            {quizzes.reduce((acc, q) => acc + q.questions.length, 0) > 1 ? "s" : ""}
          </span>
          <button
            data-testid="editor-save-button"
            onClick={save}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 rounded-xl bg-cyan-400 text-black font-display font-bold uppercase hover:bg-cyan-300 active:scale-95 disabled:opacity-50 glow-primary transition-all"
          >
            <Save className="w-4 h-4" />
            {saving ? "Enregistrement..." : editing ? "Mettre à jour" : "Créer le défi"}
          </button>
        </div>
      </div>
    </Layout>
  );
}

function Input({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.25em] text-white/50 font-mono-stat block mb-1.5">{label}</span>
      <div className="bg-black/40 border border-white/10 rounded-xl px-4 py-3 focus-within:border-cyan-400/60 focus-within:glow-primary transition-all">
        {children}
      </div>
    </label>
  );
}
